// filepath: apps/web/src/features/admin/effects/EmailChannelsPage.tsx
//
// FR-UI-ADM-8 -- main page for the email channel admin editor.
//
// INV-5 (central): `smtp_secret_value` lives only in
// `successSmtpSecretValue` state and is cleared on dialog close.

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import {
  useCreateEmailChannel,
  useDeleteEmailChannel,
  useEmailChannels,
  usePatchEmailChannel,
} from "./queries";
import { EmailChannelsTable } from "./EmailChannelsTable";
import { EmailChannelFormDialog } from "./EmailChannelFormDialog";
import { EmailChannelConflictDialog } from "./EmailChannelConflictDialog";
import { PendingReviewBanner } from "./PendingReviewBanner";
import type { CreateEmailChannelRequest, VersionConflictError } from "./types";

interface EmailChannelsPageProps {
  environmentKind: string;
}

export function EmailChannelsPage({
  environmentKind: _environmentKind,
}: EmailChannelsPageProps): React.ReactElement {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useEmailChannels();
  const createMutation = useCreateEmailChannel();
  const patchMutation = usePatchEmailChannel();
  const deleteMutation = useDeleteEmailChannel();

  const [createOpen, setCreateOpen] = useState(false);
  const [successSmtpSecretValue, setSuccessSmtpSecretValue] = useState<
    string | null
  >(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<VersionConflictError | null>(null);

  const isPending =
    createMutation.isPending ||
    patchMutation.isPending ||
    deleteMutation.isPending;

  const handleCreate = async (req: CreateEmailChannelRequest) => {
    setFormError(null);
    const resp = await createMutation.mutateAsync(req);
    setSuccessSmtpSecretValue(resp.smtp_secret_value);
  };

  const handleDisable = async (id: string, version: number) => {
    try {
      await patchMutation.mutateAsync({ id, status: "disabled", version });
    } catch (err) {
      if (err && typeof err === "object" && "code" in err) {
        setConflict(err as VersionConflictError);
      }
    }
  };

  const handleDelete = async (id: string, version: number) => {
    try {
      await deleteMutation.mutateAsync({ id, version });
    } catch (err) {
      if (err && typeof err === "object" && "code" in err) {
        setConflict(err as VersionConflictError);
      }
    }
  };

  if (isLoading) {
    return (
      <div data-testid="email-channels-loading">{t("common.loading")}</div>
    );
  }
  if (isError) {
    return (
      <div role="alert" data-testid="email-channels-error">
        {t("common.fetch_error")}
      </div>
    );
  }

  const channels = data?.channels ?? [];

  return (
    <div className="space-y-4 p-4" data-testid="email-channels-page">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">
          {t("admin.effects.email.page.title")}
        </h1>
        <button
          type="button"
          className="rounded bg-primary px-4 py-2 text-sm text-primary-foreground"
          onClick={() => setCreateOpen(true)}
          data-testid="email-channel-create-button"
        >
          {t("admin.effects.email.action.create")}
        </button>
      </div>

      <PendingReviewBanner defType="email_channel" />

      <EmailChannelsTable
        channels={channels}
        onDisable={handleDisable}
        onDelete={handleDelete}
        isPending={isPending}
      />

      <EmailChannelFormDialog
        open={createOpen}
        onSubmit={handleCreate}
        onClose={() => {
          setCreateOpen(false);
          setSuccessSmtpSecretValue(null);
          setFormError(null);
        }}
        isPending={createMutation.isPending}
        successSmtpSecretValue={successSmtpSecretValue}
        error={formError}
      />

      <EmailChannelConflictDialog
        open={conflict !== null}
        conflict={conflict}
        onRefetch={() => void 0}
        onDiscard={() => setConflict(null)}
        onClose={() => setConflict(null)}
      />
    </div>
  );
}
